"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.CreateBotAadAppDriver = void 0;
const tslib_1 = require("tslib");
const typedi_1 = require("typedi");
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const perf_hooks_1 = require("perf_hooks");
const axios_1 = tslib_1.__importDefault(require("axios"));
const common_1 = require("../../utils/common");
const lib_1 = require("@feathersjs/hooks/lib");
const addStartAndEndTelemetry_1 = require("../middleware/addStartAndEndTelemetry");
const constants_1 = require("./utility/constants");
const localizeUtils_1 = require("../../../common/localizeUtils");
const utils_1 = require("../util/utils");
const unexpectedEmptyBotPasswordError_1 = require("./error/unexpectedEmptyBotPasswordError");
const common_2 = require("../../../error/common");
const outputEnvironmentVariableUndefinedError_1 = require("../error/outputEnvironmentVariableUndefinedError");
const aadAppClient_1 = require("../aad/utility/aadAppClient");
const signInAudience_1 = require("../aad/interface/signInAudience");
const aadAppNameTooLongError_1 = require("../aad/error/aadAppNameTooLongError");
const actionName = "botAadApp/create"; // DO NOT MODIFY the name
const helpLink = "https://aka.ms/teamsfx-actions/botaadapp-create";
const successRegisterBotAad = `${actionName}/success`;
const propertyKeys = {
    reusingExistingBotAad: "reuse-existing-bot-aad",
    registerBotAadTime: "register-bot-aad-time",
};
let CreateBotAadAppDriver = class CreateBotAadAppDriver {
    constructor() {
        this.description = localizeUtils_1.getLocalizedString("driver.botAadApp.create.description");
        this.progressTitle = localizeUtils_1.getLocalizedString(constants_1.progressBarKeys.creatingBotAadApp);
    }
    async run(args, context) {
        return common_1.wrapRun(async () => {
            const result = await this.handler(args, context);
            return result.output;
        }, actionName);
    }
    async execute(args, ctx, outputEnvVarNames) {
        let summaries = [];
        const outputResult = await common_1.wrapRun(async () => {
            const result = await this.handler(args, ctx, outputEnvVarNames);
            summaries = result.summaries;
            return result.output;
        }, actionName);
        return {
            result: outputResult,
            summaries,
        };
    }
    async handler(args, context, outputEnvVarNames) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        try {
            (_a = context.logProvider) === null || _a === void 0 ? void 0 : _a.info(localizeUtils_1.getLocalizedString(constants_1.logMessageKeys.startExecuteDriver, actionName));
            this.validateArgs(args);
            if (!outputEnvVarNames) {
                throw new outputEnvironmentVariableUndefinedError_1.OutputEnvironmentVariableUndefinedError(actionName);
            }
            const aadAppClient = new aadAppClient_1.AadAppClient(context.m365TokenProvider, context.logProvider);
            const botAadAppState = utils_1.loadStateFromEnv(outputEnvVarNames);
            const isReusingExisting = !(!botAadAppState.botId || !botAadAppState.botPassword);
            // If it's the case of a valid bot id with an empty bot password, then throw an error
            if (botAadAppState.botId && !botAadAppState.botPassword) {
                throw new unexpectedEmptyBotPasswordError_1.UnexpectedEmptyBotPasswordError(actionName, helpLink);
            }
            const startTime = perf_hooks_1.performance.now();
            if (!botAadAppState.botId) {
                (_b = context.logProvider) === null || _b === void 0 ? void 0 : _b.info(localizeUtils_1.getLocalizedString(constants_1.logMessageKeys.startCreateBotAadApp));
                const aadApp = await aadAppClient.createAadApp(args.name, signInAudience_1.SignInAudience.AzureADMultipleOrgs);
                botAadAppState.botId = aadApp.appId;
                botAadAppState.botPassword = await aadAppClient.generateClientSecret(aadApp.id);
                (_c = context.logProvider) === null || _c === void 0 ? void 0 : _c.info(localizeUtils_1.getLocalizedString(constants_1.logMessageKeys.successCreateBotAadApp));
            }
            else {
                (_d = context.logProvider) === null || _d === void 0 ? void 0 : _d.info(localizeUtils_1.getLocalizedString(constants_1.logMessageKeys.skipCreateBotAadApp));
            }
            const durationMilliSeconds = perf_hooks_1.performance.now() - startTime;
            const outputs = utils_1.mapStateToEnv(botAadAppState, outputEnvVarNames);
            const successCreateBotAadLog = localizeUtils_1.getLocalizedString(constants_1.logMessageKeys.successCreateBotAad, botAadAppState.botId);
            const useExistingBotAadLog = localizeUtils_1.getLocalizedString(constants_1.logMessageKeys.useExistingBotAad, botAadAppState.botId);
            const summary = isReusingExisting ? useExistingBotAadLog : successCreateBotAadLog;
            (_e = context.logProvider) === null || _e === void 0 ? void 0 : _e.info(summary);
            (_f = context.logProvider) === null || _f === void 0 ? void 0 : _f.info(localizeUtils_1.getLocalizedString(constants_1.logMessageKeys.successExecuteDriver, actionName));
            context.telemetryReporter.sendTelemetryEvent(successRegisterBotAad, {
                [propertyKeys.reusingExistingBotAad]: isReusingExisting.toString(),
                [propertyKeys.registerBotAadTime]: durationMilliSeconds.toString(),
            });
            return {
                output: outputs,
                summaries: [summary],
            };
        }
        catch (error) {
            if (error instanceof teamsfx_api_1.UserError || error instanceof teamsfx_api_1.SystemError) {
                (_g = context.logProvider) === null || _g === void 0 ? void 0 : _g.error(localizeUtils_1.getLocalizedString(constants_1.logMessageKeys.failExecuteDriver, actionName, error.displayMessage));
                throw error;
            }
            if (axios_1.default.isAxiosError(error)) {
                const message = JSON.stringify((_h = error.response) === null || _h === void 0 ? void 0 : _h.data);
                (_j = context.logProvider) === null || _j === void 0 ? void 0 : _j.error(localizeUtils_1.getLocalizedString(constants_1.logMessageKeys.failExecuteDriver, actionName, message));
                if (error.response.status >= 400 && error.response.status < 500) {
                    throw new common_2.HttpClientError(error, actionName, message, helpLink);
                }
                else {
                    throw new common_2.HttpServerError(error, actionName, message);
                }
            }
            if (error.name === "AadCreateAppError") {
                throw new common_2.UnhandledUserError(error, actionName);
            }
            const message = JSON.stringify(error);
            (_k = context.logProvider) === null || _k === void 0 ? void 0 : _k.error(localizeUtils_1.getLocalizedString(constants_1.logMessageKeys.failExecuteDriver, actionName, message));
            throw new common_2.UnhandledError(error, actionName);
        }
    }
    validateArgs(args) {
        const invalidParameters = [];
        if (typeof args.name !== "string" || !args.name) {
            invalidParameters.push("name");
        }
        if (invalidParameters.length > 0) {
            throw new common_2.InvalidActionInputError(actionName, invalidParameters, helpLink);
        }
        if (args.name.length > 120) {
            throw new aadAppNameTooLongError_1.AadAppNameTooLongError(actionName);
        }
    }
};
tslib_1.__decorate([
    lib_1.hooks([addStartAndEndTelemetry_1.addStartAndEndTelemetry(actionName, actionName)]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object, Object]),
    tslib_1.__metadata("design:returntype", Promise)
], CreateBotAadAppDriver.prototype, "run", null);
tslib_1.__decorate([
    lib_1.hooks([addStartAndEndTelemetry_1.addStartAndEndTelemetry(actionName, actionName)]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object, Object, Map]),
    tslib_1.__metadata("design:returntype", Promise)
], CreateBotAadAppDriver.prototype, "execute", null);
CreateBotAadAppDriver = tslib_1.__decorate([
    typedi_1.Service(actionName) // DO NOT MODIFY the service name
], CreateBotAadAppDriver);
exports.CreateBotAadAppDriver = CreateBotAadAppDriver;
//# sourceMappingURL=create.js.map